import { useCallback } from 'react';
import { ensureTrimmedEmails } from '../helpers/formatters';
import type { CoHostsManagement, PersonalMeetingData, UseMeetingCoHostsParams } from '../types';

const useMeetingCoHosts = ({
  meetingData,
  onUpdate,
}: UseMeetingCoHostsParams): CoHostsManagement => {
  const updateCoHosts = useCallback(
    async (emails: string[]) => {
      const normalized = ensureTrimmedEmails(emails);
      await onUpdate(
        (prev: PersonalMeetingData) => ({
          ...prev,
          coHostEmails: normalized,
        }),
        {
          field: 'coHostEmails',
          successMessage: 'Co-host list updated',
        },
      );
    },
    [onUpdate],
  );

  const addCoHost = useCallback(
    async (email: string) => {
      if (!email) {
        return;
      }
      await updateCoHosts([...(meetingData?.coHostEmails ?? []), email]);
    },
    [meetingData?.coHostEmails, updateCoHosts],
  );

  const removeCoHost = useCallback(
    async (email: string) => {
      if (!meetingData) {
        return;
      }
      await updateCoHosts(
        meetingData.coHostEmails.filter((item: string) => item !== email.toLowerCase()),
      );
    },
    [meetingData, updateCoHosts],
  );

  return {
    updateCoHosts,
    addCoHost,
    removeCoHost,
  };
};

export default useMeetingCoHosts;
